import React, {
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  forwardRef,
  Fragment
} from 'react'
import PropTypes from 'prop-types'
import { useGlobalId, useSetState, useUpdateRef } from './hooks'
import contains from './contains'
import positionElement from './positionElement'
import anchorPoints from './anchorPoints'
import useEventHandler from './useEventHandler'

const SHOW_ON = {
  HOVER: 'hover',
  CLICK: 'click',
  FOCUS: 'focus'
}
const ALIGNMENTS = ['top', 'right', 'bottom', 'left']
const DEFER_TIME = 100

const SHOW_ON_VALUES = Object.values(SHOW_ON)

/**
 * React component to render an dropdown.
 *
 * @param {import('./interfaces').DropdownProps} props
 * @param {import('react').Ref<HTMLElement>} ref
 * @returns {JSX.Element}
 */
function dropdown (
  {
    Trigger,
    triggerProps = {},
    Container,
    Wrapper,
    isShown: propsIsShown,
    defaultIsShown,
    delayShow,
    delayHide,
    showOn: propsShowOn,
    onShow,
    onHide,
    position: propsPosition,
    children,
    wrapperId,
    withHideOnClickOutside,
    style = {},
    ...rest
  },
  ref
) {
  const id = useGlobalId('zb-dropdown-container-')
  const containerId = wrapperId || id
  const wrapperRef = useRef()
  const containerRef = useRef()
  const deferTimeout = useRef()
  const isShownFixed = useRef(propsIsShown !== undefined)
  useUpdateRef(ref, wrapperRef)

  const [
    {
      position,
      isShown,
      aboutToShow,
      showTrigger,
      calculatedPosition,
      initialRender
    },
    setState
  ] = useSetState(
    propsIsShown != null ? !!propsIsShown : !!defaultIsShown,
    isShown => ({
      isShown,
      initialRender: true,
      aboutToShow: isShown,
      showTrigger: null,
      calculatedPosition: null,
      position: normalizePosition(propsPosition)
    })
  )
  const showOn = useMemo(
    () => {
      if (propsShowOn) {
        return Array.isArray(propsShowOn)
          ? propsShowOn.filter(String)
          : [propsShowOn]
      }
      return null
    },
    [propsShowOn]
  )
  const delay = useMemo(() => {
    if (showTrigger) {
      const delayList = aboutToShow ? delayShow : delayHide
      if (Array.isArray(delayList)) {
        const triggerIndex = showOn.indexOf(showTrigger)
        return delayList[triggerIndex === -1 ? 0 : triggerIndex] || 0
      }
      return delayList
    } else {
      return 0
    }
  }, [showOn, aboutToShow, showTrigger, delayHide, delayShow])
  const isShownOn = useCallback(type => showOn && showOn.indexOf(type) !== -1, [
    showOn
  ])
  const handleToggle = useCallback((aboutToShow, showTrigger) => {
    setState({ aboutToShow, showTrigger })
  }, [])
  const createEventHandler = useMemo(
    () => (aboutToShow, showTrigger) => event => {
      if (isShownOn(showTrigger)) {
        handleToggle(aboutToShow, showTrigger)
      }
    },
    [isShownOn]
  )
  const handleFocus = useCallback(createEventHandler(true, SHOW_ON.FOCUS), [
    createEventHandler
  ])
  const handleBlur = useCallback(createEventHandler(false, SHOW_ON.FOCUS), [
    createEventHandler
  ])
  const handleEnter = useCallback(createEventHandler(true, SHOW_ON.HOVER), [
    createEventHandler
  ])
  const handleLeave = useCallback(createEventHandler(false, SHOW_ON.HOVER), [
    createEventHandler
  ])
  const handleClick = useCallback(createEventHandler(true, SHOW_ON.CLICK), [
    createEventHandler
  ])
  const handlePosition = useCallback(() => {
    if (isShown && position) {
      const calculatedPosition = positionElement(
        wrapperRef.current,
        containerRef.current,
        position,
        !initialRender
      )
      setState({ calculatedPosition, initialRender: false })
    }
  }, [isShown, position, initialRender])
  const handleDeferredPosition = useCallback(() => {
    clearTimeout(deferTimeout.current)
    if (isShown) {
      deferTimeout.current = setTimeout(handlePosition, DEFER_TIME)
    }
  }, [isShown, handlePosition])
  const handleClickOutside = useCallback(
    ({ target }) => {
      if (
        withHideOnClickOutside &&
        target !== containerRef.current &&
        !contains(containerRef.current, target) &&
        !contains(wrapperRef.current, target)
      ) {
        handleToggle(false, SHOW_ON.CLICK)
      }
    },
    [withHideOnClickOutside, handleToggle]
  )

  useEffect(() => {
    const position = normalizePosition(propsPosition)
    isShownFixed.current = propsIsShown !== undefined
    if (propsIsShown !== undefined) {
      setState({ isShown: propsIsShown, aboutToShow: propsIsShown, position })
    } else {
      setState({ position })
    }
    return () => {
      clearTimeout(deferTimeout.current)
    }
  }, [propsIsShown, propsPosition])

  useEffect(() => {
    if (aboutToShow !== isShown) {
      if (delay) {
        const timeout = setTimeout(toggleState, delay)
        return () => {
          clearTimeout(timeout)
        }
      } else toggleState()
    }
    return noop

    function toggleState () {
      const isShown = aboutToShow
      if (!isShownFixed.current) {
        setState({ isShown })
      }
      if (isShown) onShow && onShow(showTrigger)
      else onHide && onHide(showTrigger)
    }
  }, [showTrigger, aboutToShow, isShown, onShow, onHide, delay])
  useLayoutEffect(handlePosition, [handlePosition])
  useLayoutEffect(handleDeferredPosition, [handleDeferredPosition])
  useEventHandler('resize', handleDeferredPosition, document.defaultView)
  // useEventHandler('scroll', handleDeferredPosition, document.defaultView)
  useEventHandler('click', handleClickOutside)

  return (
    <Fragment>
      <Container
        style={{
          display: 'inline-block',
          ...style
        }}
        ref={containerRef}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onClick={handleClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        {...rest}
      >
        <Trigger
          {...triggerProps}
        />
        <Wrapper
          id={containerId}
          data-position={calculatedPosition}
          data-container={containerRef}
          style={{
            position: 'absolute',
            display: isShown ? 'block' : 'none'
          }}
          ref={wrapperRef}
        >
          {isShown ? children : null}
        </Wrapper>
      </Container>
    </Fragment>
  )
}

function normalizePosition (position) {
  return typeof position === 'string'
    ? positionStrToObj(position)
    : {
      direction: position.direction,
      align: position.align,
      offset: isNaN(position.offset) ? 0 : position.offset
    }
}

function positionStrToObj (positionStr) {
  const [direction, align] = positionStr.split('-')

  return {
    direction,
    align,
    offset: 0
  }
}

function noop () {}

const delayProps = PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.arrayOf(PropTypes.number)
])
const showOnProps = PropTypes.oneOf(SHOW_ON_VALUES.concat(null))
const alignmentProps = PropTypes.oneOf(ALIGNMENTS)

const Dropdown = forwardRef(dropdown)

Dropdown.displayName = 'Dropdown'

Dropdown.defaultProps = {
  Container: 'span',
  Wrapper: 'span',
  Trigger: 'button',
  showOn: null,
  position: ALIGNMENTS[2]
}

Dropdown.propTypes = {
  Trigger: PropTypes.elementType.isRequired,
  triggerProps: PropTypes.shape({
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    className: PropTypes.string
  }),
  Container: PropTypes.elementType.isRequired,
  Wrapper: PropTypes.elementType.isRequired,
  isShown: PropTypes.bool,
  defaultIsShown: PropTypes.bool,
  delayHide: delayProps,
  delayShow: delayProps,
  showOn: PropTypes.oneOfType([showOnProps, PropTypes.arrayOf(showOnProps)]),
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  position: PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(anchorPoints)),
    PropTypes.shape({
      direction: alignmentProps,
      align: alignmentProps,
      offset: PropTypes.number
    })
  ]),
  withHideOnClickOutside: PropTypes.bool
}

export default Dropdown
